import { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { publishKeys, useVersions } from '../../features/publish/useVersions'
import { AvatarBoError } from '../../shared/AvatarBoClient'
import type { AvatarBoClient, BoVersion, GateIssue } from '../../shared/types'

export interface UsePublishOpts {
  client: AvatarBoClient
  channel?: string
}

export function usePublish({ client, channel = 'prod' }: UsePublishOpts) {
  const qc = useQueryClient()
  const [gateIssues, setGateIssues] = useState<GateIssue[]>([])
  const [error, setError] = useState<string | undefined>(undefined)

  const versions = useVersions({ client, channel })

  const refresh = () => {
    qc.invalidateQueries(publishKeys.versions(channel))
  }

  const clear = () => {
    setGateIssues([])
    setError(undefined)
  }

  /**
   * A gate failure is NOT an error state: the request was understood and the catalogue is not
   * publishable. It is the screen's most useful output, so it is kept separately from `error`.
   */
  const onFailure = (e: unknown) => {
    if (e instanceof AvatarBoError && e.issues.length) {
      setGateIssues(e.issues)

      return
    }
    setError(e instanceof AvatarBoError ? e.code : String(e))
  }

  const stage = useMutation<BoVersion, unknown, void>(
    () => client.stageVersion(channel),
    { onMutate: clear, onError: onFailure, onSuccess: refresh },
  )

  const approve = useMutation<unknown, unknown, number>(
    (n: number) => client.approveVersion(n),
    { onMutate: clear, onError: onFailure, onSuccess: refresh },
  )

  const decline = useMutation<unknown, unknown, { n: number; note: string }>(
    ({ n, note }) => client.declineVersion(n, note),
    { onMutate: clear, onError: onFailure, onSuccess: refresh },
  )

  const publish = useMutation<unknown, unknown, number>(
    (n: number) => client.publishVersion(n, channel),
    { onMutate: clear, onError: onFailure, onSuccess: refresh },
  )

  return {
    versions,
    stage,
    approve,
    decline,
    publish,
    gateIssues,
    error,
    clear,
    busy:
      stage.isLoading ||
      approve.isLoading ||
      decline.isLoading ||
      publish.isLoading,
  }
}
